import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const { requestPasswordResetCode, resetPassword } = useAuth();
  const [step, setStep] = useState("request");
  const [loading, setLoading] = useState(false);
  const [expiresIn, setExpiresIn] = useState(0);
  const [formData, setFormData] = useState({
    email: "",
    code: "",
    newPassword: "",
    confirmPassword: "",
  });

  const canResend = step === "confirm" && expiresIn <= 0 && !loading;

  useEffect(() => {
    if (step !== "confirm" || expiresIn <= 0) return;
    const timer = window.setInterval(() => {
      setExpiresIn((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [step, expiresIn]);

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await requestPasswordResetCode(formData.email);
    if (result.success) {
      setStep("confirm");
      setExpiresIn(result.expiresIn || 600);
    }
    setLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (formData.newPassword !== formData.confirmPassword) {
      return;
    }
    setLoading(true);
    const result = await resetPassword(formData.email, formData.code, formData.newPassword);
    if (result.success) {
      navigate("/giris");
    }
    setLoading(false);
  };

  const handleResend = async () => {
    if (!canResend) return;
    setLoading(true);
    const result = await requestPasswordResetCode(formData.email);
    if (result.success) {
      setExpiresIn(result.expiresIn || 600);
      setFormData((prev) => ({ ...prev, code: "" }));
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-muted/50">
      <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-6 shadow-lg">
        <div className="mb-6 text-center">
          <img src="/obihub-logo.png" alt="ObiHub" className="h-9 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-foreground">Şifremi Unuttum</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {step === "request"
              ? "Şifre sıfırlama kodu almak için e-postanızı girin"
              : "E-postanıza gelen kodu girip yeni şifre belirleyin"}
          </p>
        </div>

        {step === "request" ? (
          <form onSubmit={handleRequestCode} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-medium">E-posta</Label>
              <Input
                id="email"
                type="email"
                placeholder="ornek@mail.com"
                value={formData.email}
                onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                required
                className="h-10"
              />
            </div>
            <Button type="submit" className="w-full h-10 bg-[#1487a3] hover:bg-[#1e7ab3]" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Kod Gönder
            </Button>
          </form>
        ) : (
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="code" className="text-xs font-medium">Doğrulama Kodu</Label>
              <Input
                id="code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                placeholder="6 haneli kod"
                value={formData.code}
                onChange={(e) => setFormData((prev) => ({ ...prev, code: (e.target.value || "").replace(/\D/g, "").slice(0, 6) }))}
                required
                className="h-10"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="newPassword" className="text-xs font-medium">Yeni Şifre</Label>
              <Input
                id="newPassword"
                type="password"
                minLength={6}
                placeholder="En az 6 karakter"
                value={formData.newPassword}
                onChange={(e) => setFormData((prev) => ({ ...prev, newPassword: e.target.value }))}
                required
                className="h-10"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-xs font-medium">Yeni Şifre (Tekrar)</Label>
              <Input
                id="confirmPassword"
                type="password"
                minLength={6}
                placeholder="Yeni şifreyi tekrar girin"
                value={formData.confirmPassword}
                onChange={(e) => setFormData((prev) => ({ ...prev, confirmPassword: e.target.value }))}
                required
                className="h-10"
              />
            </div>
            <Button
              type="submit"
              className="w-full h-10 bg-[#1487a3] hover:bg-[#1e7ab3]"
              disabled={loading || formData.code.length !== 6 || formData.newPassword !== formData.confirmPassword}
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Şifreyi Güncelle
            </Button>
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <button type="button" className="hover:text-foreground disabled:opacity-50" disabled={!canResend} onClick={handleResend}>
                Kodu yeniden gönder
              </button>
              <span>{expiresIn > 0 ? `${expiresIn}s` : "Tekrar gönderebilirsiniz"}</span>
            </div>
          </form>
        )}

        <div className="mt-5 text-center text-sm">
          <Link to="/giris" className="text-[#1487a3] hover:underline font-medium">Girişe dön</Link>
        </div>
      </div>
    </div>
  );
}
